import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Link } from 'react-router-dom';
import { isInstalledApp } from '../../utils/pwa';
import { useForm, Controller } from "react-hook-form";
import { toast } from 'react-toastify';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';

// Custom components
import CustomTextInput from '../../components/formComponents/CustomTextInput';
import CustomPasswordInput from '../../components/formComponents/CustomPassword';
import CustomCheckbox from '../../components/formComponents/CustomCheckbox';
import CustomButton from '../../components/common/CustomButton';

// Auth context
import { useAuth } from '../../context/AuthContext';
import { useProfile } from '../../context/ProfileContext';
import { merchantAuthController } from '../../controllers/authController';

interface FormData {
  email: string;
  password: string;
  rememberMe: boolean;
}

// Define validation schema
const schema = yup.object({
  email: yup
    .string()
    .email('Please enter a valid email')
    .required('Email is required'),
  password: yup
    .string()
    .required('Password is required'),
  rememberMe: yup.boolean().default(false)
}).required() as any;

const Login: React.FC = () => {
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(false);
  const { login } = useAuth();
  const { setProfile } = useProfile();

  const {
    control,
    handleSubmit,
    formState: { errors }
  } = useForm<FormData>({
    resolver: yupResolver(schema) as any,
    defaultValues: {
      email: '',
      password: '',
      rememberMe: false
    }
  });

  const onSubmit = async (data: FormData) => {
    try {
      setIsLoading(true);
      const result = await login(data.email, data.password);

      if (result.success) {
        toast.success('Login successful!');
        // Fetch profile info
        try {
          const profileRes = await merchantAuthController.getProfileInfo();
          setProfile(profileRes.data?.data || profileRes.data); // adjust as per API response shape
        } catch (profileErr) {
          toast.error('Failed to fetch profile info');
          console.error('Profile fetch error:', profileErr);
        }
        // Send the merchant straight to the dashboard after login.
        navigate('/app/home', { replace: true });
      } else {
        throw result.error;
      }
    } catch (error: any) {
      let errorMessage = 'An unexpected error occurred';

      if (error.response?.status === 401) {
        errorMessage = 'Invalid email or password';
      } else if (error.response?.status === 404) {
        errorMessage = 'Account not found';
      } else if (error.response?.data?.message) {
        errorMessage = error.response.data.message;
      } else if (error.response?.data?.errors?.[0]) {
        errorMessage = error.response.data.errors[0];
      }

      toast.error(errorMessage);
      console.error('Login error:', error);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="md:w-1/2 w-full flex justify-center items-center md:px-8 md:max-h-screen overflow-auto">
      <div className="p-6 md:p-8 flex flex-col justify-center items-center gap-3 md:gap-4 text-black text-center max-w-[480px] w-full bg-white md:bg-transparent rounded-2xl md:rounded-none shadow-lg md:shadow-none">
        {/* Welcome Back Heading */}
        <h1 className="text-xl md:text-4xl font-bold font-sk-modernist">
          Welcome Back!
        </h1>

        {/* Subtitle */}
        <p className="text-sm md:text-base font-sk-modernist text-styola-gray mb-1 md:mb-2 text-center">
          Welcome Back! Please log in to access your dashboard and manage your store.
        </p>

        <form onSubmit={handleSubmit(onSubmit)} className="w-full">
          <div className="flex flex-col gap-3 md:gap-4 w-full">
            <Controller
              name="email"
              control={control}
              render={({ field }) => (
                <CustomTextInput
                  {...field}
                  type="email"
                  placeholder="Email Address*"
                  className="border border-gray-300 rounded-md font-sk-modernist"
                  autoFocus
                  error={errors.email?.message}
                />
              )}
            />
            <Controller
              name="password"
              control={control}
              render={({ field }) => (
                <CustomPasswordInput
                  {...field}
                  className="border border-gray-300 rounded-md font-sk-modernist"
                  placeholder="Password*"
                  error={errors.password?.message}
                />
              )}
            />
            <div>
              <div className="flex flex-wrap gap-3 justify-between items-center mb-3 md:mb-4">
                <Controller
                  name="rememberMe"
                  control={control}
                  render={({ field }) => (
                    <CustomCheckbox
                      {...field}
                      checked={field.value}
                      onChange={field.onChange}
                      className="items-center"
                      labelClassName="!text-sm !font-medium mt-1 font-sk-modernist"
                      label={<div className="font-sk-modernist">Keep me Signed In</div>}
                      error={errors.rememberMe?.message}
                    />
                  )}
                />
                <Link to="/forgot-password">
                  <div className="text-sm font-bold text-styola-primary font-sk-modernist">
                    Forgot Password?
                  </div>
                </Link>
              </div>

              <CustomButton
                title="Login"
                type="submit"
                variant="secondary"
                className="w-full font-sk-modernist"
                isLoading={isLoading}
              />
            </div>
          </div>
        </form>

        {/* Google Auth - Disabled for now */}
        {/* OR Divider
        <div className="flex items-center w-full my-2">
          <div className="flex-1 border-t border-gray-300"></div>
          <span className="px-4 text-sm text-gray-500 font-sk-modernist">OR</span>
          <div className="flex-1 border-t border-gray-300"></div>
        </div>
        */}

        {/* Google Login Button
        <button
          type="button"
          className="w-full flex items-center justify-center gap-3 py-3 px-4 border border-gray-300 rounded-md hover:bg-gray-50 transition-colors font-sk-modernist"
        >
          <img
            src="https://www.gstatic.com/firebasejs/ui/2.0.0/images/auth/google.svg"
            alt="Google"
            className="w-5 h-5"
          />
          <span className="text-black text-sm md:text-base">Login with Google</span>
        </button>
        */}

        {/* Register Link. The installed app is for merchants who already have an account. */}
        {!isInstalledApp() && (
          <p className="text-sm mt-2 text-center text-black font-sk-modernist">
            New User?{" "}
            <Link to="/register" className="text-styola-primary font-bold font-sk-modernist">
              Create Account
            </Link>
          </p>
        )}
      </div>
    </div>
  );
};

export default Login;
